import { useState } from 'react';
import { useAtomValue } from 'jotai';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { remoteAccessContextAtom } from '@/store/remoteAccessContextAtom';
import { patientProfileAtom } from '@/store/patientProfileAtom';

const DISMISSED_KEY = 'remoteAccessBannerDismissed';

/**
 * Persistent warning bar shown when the portal was opened by an admin via a remote access token.
 * Dismissible for the current tab session; reappears on page reload (sessionStorage is
 * cleared when the tab is closed).
 */
export function RemoteAccessBanner() {
  const remoteAccess = useAtomValue(remoteAccessContextAtom);
  const profile      = useAtomValue(patientProfileAtom);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISSED_KEY) === '1',
  );

  if (!remoteAccess || dismissed) return null;

  const patientLabel = profile?.firstName
    ? `${profile.firstName} ${profile.lastName ?? ''}`.trim()
    : `patient key: ${remoteAccess.personKey}`;

  const handleClose = () => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
  };

  return (
    <Box sx={{ position: 'sticky', top: 0, zIndex: 1300 }}>
      <Alert
        severity="warning"
        onClose={handleClose}
        sx={{
          borderRadius: 0,
          py: 0.5,
          fontSize: 13,
          '& .MuiAlert-message': { display: 'flex', alignItems: 'center' },
        }}
      >
        <strong>Remote access session</strong>&nbsp;{remoteAccess.adminUserId}&nbsp;— viewing {patientLabel}
      </Alert>
    </Box>
  );
}
